import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { getAuth } from 'firebase/auth';
import FeeStructureSection from './FeeStructureSection';
import DiscountPolicyDisplay from './DiscountPolicyDisplay';
import FeeBreakdownWithDiscounts from './FeeBreakdownWithDiscounts';
import { CRMIntegrationService } from '@obis/integration';
import { ClassesService, Class } from '@obis/firebase-services';

interface AddApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplicationAdded: () => void;
  branchId: string;
}

const AddApplicationModal: React.FC<AddApplicationModalProps> = ({
  isOpen,
  onClose,
  onApplicationAdded,
  branchId
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'documents'>('details');
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [studentName, setStudentName] = useState('');
  const [applicationCreated, setApplicationCreated] = useState(false);
  const [token, setToken] = useState<string>('');
  const [discountData, setDiscountData] = useState<any>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [formData, setFormData] = useState({
    studentName: '',
    studentDOB: '',
    gender: '',
    religion: '',
    motherTongue: '',
    aadharNo: '',
    parentName: '',
    motherName: '',
    parentEmail: '',
    parentPhone: '',
    fatherProfession: '',
    applyingForGrade: '',
    previousSchool: '',
    lastClassAttended: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [feeStructure, setFeeStructure] = useState<any>(null);
  const [feeStructureLoading, setFeeStructureLoading] = useState(false);
  const [discountSelection, setDiscountSelection] = useState<any>(null);

  // Get token when modal opens
  React.useEffect(() => {
    if (isOpen && !token) {
      const getToken = async () => {
        try {
          const auth = getAuth();
          const currentUser = auth.currentUser;
          if (currentUser) {
            const freshToken = await currentUser.getIdToken(true);
            setToken(freshToken);
          }
        } catch (err) {
          console.error('Error getting token:', err);
        }
      };
      getToken();
    }
  }, [isOpen, token]);

  // Fetch classes from Firestore
  React.useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoadingClasses(true);
        console.log('📚 AddApplicationModal: Fetching classes...');
        const fetchedClasses = await ClassesService.getClasses();
        console.log('📚 AddApplicationModal: Fetched classes:', fetchedClasses);
        setClasses(fetchedClasses);
        if (fetchedClasses.length === 0) {
          console.warn('⚠️ No classes returned from service');
        }
      } catch (err) {
        console.error('❌ Error fetching classes:', err);
        setClasses([]);
      } finally {
        setLoadingClasses(false);
      }
    };
    
    if (isOpen) {
      console.log('📚 Modal opened, fetching classes...');
      fetchClasses();
    }
  }, [isOpen]);

  // Load fee structure when class or gender changes
  useEffect(() => {
    if (formData?.applyingForGrade && formData?.gender) {
      loadFeeStructure();
    }
  }, [formData?.applyingForGrade, formData?.gender]);

  const loadFeeStructure = async () => {
    if (!formData?.applyingForGrade || !formData?.gender) return;
    setFeeStructureLoading(true);
    try {
      const structure = await CRMIntegrationService.getFeeStructureFromERP(
        formData.applyingForGrade,
        formData.gender
      );
      setFeeStructure(structure);
    } catch (error) {
      console.error('Error loading fee structure:', error);
      setFeeStructure(null);
    } finally {
      setFeeStructureLoading(false);
    }
  };

  const grades = [
    'Nursery', 'Jr.KG', 'Sr.KG', 'Class I', 'Class II', 'Class III', 'Class IV', 'Class V', 'Class VI',
    'Class VII'
  ];

  const lastClassOptions = [
    'Playgroup', 'Nursery', 'Jr.KG', 'Sr.KG', 'Class I', 'Class II', 'Class III', 'Class IV', 'Class V', 'Class VI',
    'Class VII'
  ];

  const religions = [
    'Hindu', 'Islam', 'Buddhism', 'Christian', 'Sikh', 'Jain', 'Parsi', 'Others'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${API_BASE_URL}/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          branchId,
          discount: discountData || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create application');
      }

      // Store application ID and student name, then switch to documents tab
      const appId = data.applicationId || data.data?.applicationId;
      if (!appId) {
        throw new Error('No application ID returned from server');
      }
      setApplicationId(appId);
      setStudentName(formData.studentName);
      setApplicationCreated(true);
      setActiveTab('documents');
    } catch (err: any) {
      setError(err.message || 'Failed to create application');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentsComplete = () => {
    // Reset form and close modal
    setFormData({
      studentName: '',
      studentDOB: '',
      gender: '',
      religion: '',
      motherTongue: '',
      aadharNo: '',
      parentName: '',
      motherName: '',
      parentEmail: '',
      parentPhone: '',
      fatherProfession: '',
      applyingForGrade: '',
      previousSchool: '',
      lastClassAttended: '',
      address: ''
    });
    setApplicationId(null);
    setStudentName('');
    setApplicationCreated(false);
    setActiveTab('details');
    onApplicationAdded();
    onClose();
  };

  const handleCloseModal = () => {
    if (applicationCreated) {
      // If we're in documents tab, ask for confirmation
      if (window.confirm('Close without finishing document upload? You can add documents later.')) {
        setFormData({
          studentName: '',
          studentDOB: '',
          gender: '',
          religion: '',
          motherTongue: '',
          aadharNo: '',
          parentName: '',
          motherName: '',
          parentEmail: '',
          parentPhone: '',
          fatherProfession: '',
          applyingForGrade: '',
          previousSchool: '',
          lastClassAttended: '',
          address: ''
        });
        setApplicationId(null);
        setStudentName('');
        setApplicationCreated(false);
        setActiveTab('details');
        onApplicationAdded();
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900">New Application</h3>
          <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        {applicationCreated && (
          <div className="flex gap-4 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Application Details
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'documents'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <DocumentIcon className="h-4 w-4" />
              Documents
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Student Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Student Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.studentName}
                  onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                  className="input-field"
                  placeholder="Jane Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender *
                </label>
                <select
                  required
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select Gender</option>
                  <option value="Boys">Boys</option>
                  <option value="Girls">Girls</option>
                  <option value="All Sections">All Sections (Pre-Primary)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Pre-Primary: Select "All Sections" | Primary: Select "Boys" or "Girls"
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth *
                </label>
                <input
                  type="text"
                  required
                  value={formData.studentDOB}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d]/g, '');
                    if (value.length > 2 && value.length <= 4) {
                      value = value.slice(0, 2) + '-' + value.slice(2);
                    } else if (value.length > 4) {
                      value = value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4, 8);
                    }
                    setFormData({ ...formData, studentDOB: value });
                  }}
                  className="input-field"
                  placeholder="DD-MM-YYYY"
                  maxLength={10}
                />
                <p className="text-xs text-gray-500 mt-1">Format: DD-MM-YYYY (e.g., 15-08-2015)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Religion
                </label>
                <select
                  value={formData.religion}
                  onChange={(e) => setFormData({ ...formData, religion: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select Religion</option>
                  {religions.map(religion => (
                    <option key={religion} value={religion}>{religion}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mother Tongue
                </label>
                <input
                  type="text"
                  value={formData.motherTongue}
                  onChange={(e) => setFormData({ ...formData, motherTongue: e.target.value })}
                  className="input-field"
                  placeholder="Hindi, English, Bengali, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Aadhar Number
                </label>
                <input
                  type="text"
                  value={formData.aadharNo}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d]/g, '');
                    if (value.length > 12) value = value.slice(0, 12);
                    setFormData({ ...formData, aadharNo: value });
                  }}
                  className="input-field"
                  placeholder="XXXX XXXX XXXX"
                  maxLength={12}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Applying for Grade *
                </label>
                <select
                  required
                  value={formData.applyingForGrade}
                  onChange={(e) => setFormData({ ...formData, applyingForGrade: e.target.value })}
                  className="input-field"
                  disabled={loadingClasses}
                >
                  <option value="">{loadingClasses ? 'Loading classes...' : 'Select Grade'}</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.name}>{cls.name}</option>
                  ))}
                </select>
              </div>

              {/* Fee Structure Display */}
              <div className="md:col-span-2">
                {formData?.applyingForGrade && formData?.gender ? (
                  <>
                    {feeStructureLoading ? (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                        ⏳ Loading fee structure...
                      </div>
                    ) : feeStructure ? (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                        <div className="text-sm font-semibold text-green-800">✅ Fee Structure Found</div>
                        <div className="grid grid-cols-2 gap-3 text-sm text-green-700">
                          <div><strong>Monthly Fee:</strong> ₹{feeStructure.tuitionFee}</div>
                          <div><strong>Admission:</strong> ₹{feeStructure.admissionFeeMin}-{feeStructure.admissionFeeMax}</div>
                          <div><strong>Uniform:</strong> ₹{feeStructure.uniformFee}</div>
                          <div><strong>Books:</strong> ₹{feeStructure.booksAndCopies}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                        ⚠️ No fee structure found for {formData?.applyingForGrade} ({formData?.gender})
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                    Please select a class and gender to view fee structure
                  </div>
                )}
              </div>

              {/* Discount Policy Display */}
              {feeStructure && feeStructure.discountPolicy && (
                <div className="md:col-span-2">
                  <DiscountPolicyDisplay
                    discountPolicy={feeStructure.discountPolicy}
                    onDiscountChange={setDiscountSelection}
                  />
                </div>
              )}

              {/* Fee Breakdown with Discounts */}
              {feeStructure && discountSelection && (
                <div className="md:col-span-2">
                  <FeeBreakdownWithDiscounts
                    feeStructure={feeStructure}
                    discountSelection={discountSelection}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Previous School
                </label>
                <input
                  type="text"
                  value={formData.previousSchool}
                  onChange={(e) => setFormData({ ...formData, previousSchool: e.target.value })}
                  className="input-field"
                  placeholder="ABC School"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Class Attended
                </label>
                <select
                  value={formData.lastClassAttended}
                  onChange={(e) => setFormData({ ...formData, lastClassAttended: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select Class</option>
                  {lastClassOptions.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Fee Structure & Discount Section */}
          {token && (
            <FeeStructureSection
              selectedClass={formData.applyingForGrade}
              selectedGender={formData.gender}
              onDiscountApplied={setDiscountData}
              token={token}
            />
          )}

          {/* Parent Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Parent/Guardian Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Father Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.parentName}
                  onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                  className="input-field"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mother Name
                </label>
                <input
                  type="text"
                  value={formData.motherName}
                  onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                  className="input-field"
                  placeholder="Jane Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Father Profession
                </label>
                <input
                  type="text"
                  value={formData.fatherProfession}
                  onChange={(e) => setFormData({ ...formData, fatherProfession: e.target.value })}
                  className="input-field"
                  placeholder="Engineer, Doctor, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.parentEmail}
                  onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                  className="input-field"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.parentPhone}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d+]/g, '');
                    if (!value.startsWith('+91')) {
                      if (value.startsWith('91')) {
                        value = '+' + value;
                      } else if (value.startsWith('+')) {
                        value = '+91';
                      } else if (value.length > 0) {
                        value = '+91' + value;
                      }
                    }
                    if (value.length > 13) {
                      value = value.substring(0, 13);
                    }
                    setFormData({ ...formData, parentPhone: value });
                  }}
                  className="input-field"
                  placeholder="+91XXXXXXXXXX"
                />
                <p className="text-xs text-gray-500 mt-1">Format: +91XXXXXXXXXX (10 digits)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Present Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-field"
                  placeholder="123 Main St, City"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleCloseModal}
              className="btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Application'}
            </button>
          </div>
          </form>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && applicationId && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                Application created successfully! Now you can upload documents.
              </p>
            </div>
            
            {/* Embedded Documents Component */}
            <DocumentsTabContent 
              applicationId={applicationId}
              studentName={studentName}
              onComplete={handleDocumentsComplete}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Simplified documents tab content (embedded in modal)
const DocumentsTabContent: React.FC<{
  applicationId: string;
  studentName: string;
  onComplete: () => void;
}> = ({ applicationId, studentName, onComplete }) => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('birth_certificate');
  const [dragActive, setDragActive] = useState(false);

  const DOCUMENT_TYPES = [
    { type: 'birth_certificate', label: 'Birth Certificate' },
    { type: 'aadhar_card', label: 'Aadhar Card' },
    { type: 'transfer_certificate', label: 'Transfer Certificate' },
    { type: 'previous_school_cert', label: 'Previous School Certificate' },
    { type: 'medical_report', label: 'Medical Report' },
    { type: 'passport', label: 'Passport' },
    { type: 'vaccination_cert', label: 'Vaccination Certificate' },
    { type: 'other', label: 'Other Documents' }
  ];

  React.useEffect(() => {
    fetchDocuments();
  }, [applicationId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }
      const token = await currentUser.getIdToken(true);

      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setDocuments(data.data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setUploading(true);
      // Get fresh token from Firebase
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }
      const token = await currentUser.getIdToken(true);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', selectedDocType);

      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setDocuments([...documents, data.data]);
      alert('Document uploaded successfully!');
    } catch (err: any) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }
      const token = await currentUser.getIdToken(true);

      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDocuments(documents.filter(d => d.documentId !== documentId));
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upload Section */}
        <div className="lg:col-span-1">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Upload Document</h4>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type
              </label>
              <select
                value={selectedDocType}
                onChange={(e) => setSelectedDocType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {DOCUMENT_TYPES.map(doc => (
                  <option key={doc.type} value={doc.type}>
                    {doc.label}
                  </option>
                ))}
              </select>
            </div>

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <p className="text-xs text-gray-600 mb-2">Drag file here or</p>
              <label className="inline-block">
                <input
                  type="file"
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="hidden"
                />
                <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 cursor-pointer inline-block">
                  {uploading ? 'Uploading...' : 'Browse'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Uploaded ({documents.length})</h4>
          {loading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : documents.length === 0 ? (
            <p className="text-sm text-gray-500">No documents yet</p>
          ) : (
            <div className="space-y-2">
              {documents.map(doc => (
                <div key={doc.documentId} className="border border-gray-200 rounded p-3 flex justify-between items-center">
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">{doc.fileName}</p>
                    <p className="text-xs text-gray-500">{doc.documentType}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument(doc.documentId)}
                    className="text-red-600 hover:text-red-900 text-sm"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={onComplete}
          className="btn-primary"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default AddApplicationModal;
